/**
 * exportImage — PNG export for the EDG / FSM, on React Flow (phase 6e).
 *
 * The drop-in replacement for the old `lib/joint/images.js` exporters (the LAST
 * Rappid consumers). The OLD path built a hidden JointJS paper, called
 * `paper.toPNG()` (Rappid's SVG -> canvas rasteriser) and returned the content
 * bbox + a Blob. We reproduce that contract on React Flow:
 *
 *   - mount a RENDER-ONLY EdgFlow / FsmFlow into an off-screen container (real
 *     createRoot — the markers/labels/edges must actually render so html-to-image
 *     can clone them),
 *   - wait for the flow to paint + `document.fonts.ready` (Arial metrics decide
 *     label widths, so a missing font would shift the bbox),
 *   - pin the viewport at zoom 1 (the OLD `toPNG` used scale 1, no padding) and
 *     measure the rendered CONTENT bbox in viewport pixels — the union of every
 *     node rect, the edges-SVG `getBBox()` (which includes the marker glyphs and
 *     path stroke), and the HTML edge-label rects (labels render through
 *     `EdgeLabelRenderer` into a sibling div, OUTSIDE the edges svg). This union
 *     is the React Flow equivalent of Rappid's `getContentBBox`,
 *   - translate the viewport so the content's top-left sits at the origin, then
 *     `html-to-image.toPng` the `.react-flow__viewport` at that exact width/height
 *     with a white background and pixelRatio 1 (scale 1 => 1px per graph unit,
 *     matching the OLD output dimensions),
 *   - decode the data URL to a Blob and unmount + remove the container.
 *
 * Output contract (verified against live OLD exports, see phase-6e notes):
 *   - image dimensions == content bbox width x height at scale 1 (a few px of AA
 *     wiggle is acceptable; the marker/stroke extents match the old paper),
 *   - white background, no padding,
 *   - the returned `bbox` is the content bbox in FLOW coordinates ({ x, y, width,
 *     height }); the MXP zip writer only reads `bbox.x` / `bbox.y` for the file
 *     name, so flow coordinates (== old paper coordinates here, the graph is drawn
 *     1:1) keep the same `EDG_<x>x<y>.png` naming.
 *
 * The exported functions keep the SAME signatures + return shape the call sites
 * (ExportImageModal, the MXP writer in stores/projects/model_version.js) use
 * today, so the call sites only change their import path.
 *
 * Classic JSX runtime (app-wide esbuild transform) needs React in scope.
 */
import React from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { toPng } from 'html-to-image';
import type { Notation } from './ObjectTypeNode';
import { ExportEdg, ExportFsm } from './ExportFlows';

/** The bbox/image pair the old exporters returned (and the call sites destructure). */
export type ExportedImage = {
  /** Content bounding box in graph coordinates (matches the old paper bbox). */
  bbox: { x: number; y: number; width: number; height: number };
  /** The rendered PNG. */
  image: Blob;
};

// The off-screen mount is generously sized so the (un-fitted, zoom-1) flow paints
// its whole content without internal clipping before we measure + crop. React Flow
// only renders nodes within the pane viewport, so the pane must be large enough to
// contain the graph at zoom 1 translated to the origin.
const OFFSCREEN_WIDTH = 4000;
const OFFSCREEN_HEIGHT = 4000;

/** Resolve once the browser has painted at least one frame (double rAF). */
function nextPaint(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });
}

/** Wait for fonts (Arial metrics drive label widths -> the bbox). Never rejects. */
function fontsReady(): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const fonts = (document as any).fonts;
  if (fonts && typeof fonts.ready?.then === 'function') {
    return fonts.ready.then(() => undefined).catch(() => undefined);
  }
  return Promise.resolve();
}

/** Convert a data URL to a Blob (replaces the old `dataURItoBlob`). */
function dataURLtoBlob(dataURL: string): Blob {
  const [header, body] = dataURL.split(',');
  const isBase64 = header.indexOf('base64') >= 0;
  const byteString = isBase64 ? atob(body) : decodeURIComponent(body);
  const mimeMatch = header.match(/data:([^;]+)/);
  const mime = mimeMatch ? mimeMatch[1] : 'image/png';
  const bytes = new Uint8Array(byteString.length);
  for (let i = 0; i < byteString.length; i++) bytes[i] = byteString.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

type Bounds = { minX: number; minY: number; maxX: number; maxY: number };

function emptyBounds(): Bounds {
  return { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity };
}

function extend(b: Bounds, left: number, top: number, right: number, bottom: number): void {
  if (left < b.minX) b.minX = left;
  if (top < b.minY) b.minY = top;
  if (right > b.maxX) b.maxX = right;
  if (bottom > b.maxY) b.maxY = bottom;
}

/**
 * Measure the rendered content bbox (in viewport-local pixels, at zoom 1) by
 * unioning every painted element inside `.react-flow__viewport`:
 *  - node wrappers (`.react-flow__node`) -> their box incl. the 2px object-type /
 *    1px state stroke (the node DOM box already includes the SVG it wraps),
 *  - the edges `<svg>` `getBBox()` -> path stroke + marker glyphs in user space,
 *  - the HTML edge labels (`.react-flow__edgelabel-renderer > *`) -> label boxes.
 *
 * At zoom 1 the viewport transform is a pure translate, so a child's offset from
 * the viewport's own client rect IS its position in the cropped image. We measure
 * against the viewport client rect so the translate cancels out and we get a crop
 * box that is independent of the (arbitrary) starting translate.
 */
function measureContentBounds(viewport: HTMLElement): Bounds {
  const origin = viewport.getBoundingClientRect();
  const bounds = emptyBounds();

  const addRect = (r: DOMRect) => {
    extend(bounds, r.left - origin.left, r.top - origin.top, r.right - origin.left, r.bottom - origin.top);
  };

  // Nodes.
  viewport.querySelectorAll('.react-flow__node').forEach((node) => {
    addRect((node as HTMLElement).getBoundingClientRect());
  });

  // Edges svg: getBBox() is in the svg's user space (== flow units at zoom 1).
  // Convert its corners to client px via the svg's own client rect + bbox offset.
  viewport.querySelectorAll('svg.react-flow__edges').forEach((svgEl) => {
    const svg = svgEl as SVGSVGElement;
    let bbox: { x: number; y: number; width: number; height: number } | null = null;
    try {
      bbox = svg.getBBox();
    } catch {
      bbox = null;
    }
    if (!bbox || (bbox.width === 0 && bbox.height === 0)) return;
    // The svg fills the viewport with a 1:1 user-space mapping (React Flow renders
    // edges in a viewport-sized svg with no extra transform), so user-space (x,y)
    // maps to client px via the svg's client rect origin.
    const svgRect = svg.getBoundingClientRect();
    const left = svgRect.left + bbox.x - origin.left;
    const top = svgRect.top + bbox.y - origin.top;
    extend(bounds, left, top, left + bbox.width, top + bbox.height);
  });

  // HTML edge labels (rendered outside the edges svg by EdgeLabelRenderer).
  viewport
    .querySelectorAll('.react-flow__edgelabel-renderer > *')
    .forEach((label) => addRect((label as HTMLElement).getBoundingClientRect()));

  return bounds;
}

/**
 * The core: mount `element` off-screen, wait for paint + fonts, measure the
 * content bbox, crop to it, rasterise the viewport to a PNG, and clean up.
 */
async function renderToImage(element: React.ReactElement): Promise<ExportedImage> {
  const container = document.createElement('div');
  container.setAttribute('data-merode-export', '');
  // Off-screen but laid out (not display:none — html-to-image needs real layout
  // and React Flow needs a measurable pane). Absolute + far off-viewport.
  container.style.cssText = [
    'position:absolute',
    'top:0',
    'left:0',
    `width:${OFFSCREEN_WIDTH}px`,
    `height:${OFFSCREEN_HEIGHT}px`,
    'transform:translateX(-200000px)',
    'pointer-events:none',
    'background:#ffffff',
    'overflow:hidden',
  ].join(';');
  document.body.appendChild(container);

  const root: Root = createRoot(container);

  try {
    root.render(element);

    // Let React commit + React Flow measure the pane + paint a frame.
    await nextPaint();
    await fontsReady();
    await nextPaint();

    const viewport = container.querySelector('.react-flow__viewport') as HTMLElement | null;
    if (!viewport) {
      throw new Error('exportImage: React Flow viewport did not render');
    }

    const bounds = measureContentBounds(viewport);
    if (!Number.isFinite(bounds.minX) || !Number.isFinite(bounds.maxX)) {
      throw new Error('exportImage: no content to export');
    }

    const width = Math.max(1, Math.round(bounds.maxX - bounds.minX));
    const height = Math.max(1, Math.round(bounds.maxY - bounds.minY));

    // Read the current viewport translate (zoom is pinned to 1 by the off-screen
    // EdgFlow/FsmFlow, see ExportEdg/ExportFsm below) and shift it so the content's
    // top-left lands at the viewport origin -> the crop box starts at (0,0).
    const style = window.getComputedStyle(viewport);
    const matrix = new DOMMatrixReadOnly(style.transform === 'none' ? '' : style.transform);
    const newTx = matrix.e - bounds.minX;
    const newTy = matrix.f - bounds.minY;
    viewport.style.transform = `translate(${newTx}px, ${newTy}px) scale(1)`;

    // Repaint with the new transform before capturing.
    await nextPaint();

    const dataUrl = await toPng(viewport, {
      backgroundColor: '#ffffff',
      width,
      height,
      pixelRatio: 1,
      // Pin the cloned viewport's own transform so html-to-image rasterises the
      // crop box from the origin (it clones `viewport`, whose transform we just set).
      style: { transform: viewport.style.transform },
      cacheBust: true,
    });

    // The content bbox in FLOW coordinates (== graph coords at zoom 1). We recover
    // the flow-space top-left from the pre-shift translate: a viewport child at
    // client offset `bounds.minX` sits at flow x = (bounds.minX - tx) (zoom 1).
    const flowX = bounds.minX - matrix.e;
    const flowY = bounds.minY - matrix.f;

    return {
      bbox: { x: flowX, y: flowY, width, height },
      image: dataURLtoBlob(dataUrl),
    };
  } finally {
    root.unmount();
    if (container.parentNode) container.parentNode.removeChild(container);
  }
}

/**
 * Export a PNG of the EDG for `model` in `notation`. Same signature + return shape
 * as the old `lib/joint` exporter.
 */
export function exportEDGImage(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  model: any,
  notation: Notation
): Promise<ExportedImage> {
  return renderToImage(<ExportEdg model={model} notation={notation} />);
}

/**
 * Export a PNG of the given FSM. `fsm` is the FSM metamodel object; we resolve the
 * uimodel FSM view the same way the old exporter did (`model.uimodel.findFSM`).
 * Same signature + return shape as the old `lib/joint` exporter.
 */
export function exportFSMImage(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  model: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fsm: any
): Promise<ExportedImage> {
  const fsmView = model.uimodel.findFSM(fsm);
  return renderToImage(<ExportFsm fsmView={fsmView} />);
}
